import React, {
    ReactElement,
} from "react";
import { Form } from "react-bootstrap";
import classNames from "classnames";
import { FeedbackType, GetSettingsDocument, useSaveSettingsMutation } from "../../../../__generated__/api";
import Editor from "../../../../components/agent-v3/editor/Editor";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import Select from "../../../../components/select/Select";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../contexts/HelpContext";
import { SettingsProps } from "../base";
import Styles from "./Feedback.module.scss";

const FEEDBACK_TYPES = {
    [FeedbackType.None]: {
        text: "None",
        valueLabels: [],
    },
    [FeedbackType.Ces]: {
        text: "Customer Effort Score (CES)",
        valueLabels: [
            "Strongly disagree",
            "Disagree",
            "Somewhat disagree",
            "Neutral",
            "Somewhat agree",
            "Agree",
            "Strongly agree",
        ],
    },
    [FeedbackType.Nps]: {
        text: "Net Promoter Score (NPS)",
        valueLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    },
    [FeedbackType.Thumbs]: {
        text: "Thumbs Up/Thumbs Down (CSAT)",
        valueLabels: [
            "👎",
            "👍",
        ],
    },
    /// /:angry::white_frowning_face::neutral_face::slightly_smiling_face::star-struck:

    [FeedbackType.Faces]: {
        text: "Faces (CSAT)",
        valueLabels: [
            "😠",
            "🙁",
            "😐",
            "🙂",
            "🤩",
        ],
    },
};
const OPTIONS = Object.entries(FEEDBACK_TYPES).map(([key, text]) => ({
    label: text.text,
    value: key,
}));

export default function Feedback(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:f9c2e9addfc0407aa06fc692e30b1971");

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("feedback", () => props.settings?.feedback?.type ?? FeedbackType.None)
            .property("customMessage", () => props.settings?.feedback?.customMessage ?? "")
            .property("shouldAllowComments", () => props.settings?.feedback?.shouldAllowComments ?? true)
            .property(
                "responses",
                () => props.settings?.feedback?.responses?.map(row => ({ ...row })) ?? [],
                (left, right) => left?.map(
                    row => `${row?.message}-${row.value}`,
                ).equals(right?.map(row => `${row?.message}-${row.value}`) ?? []),
            ),

        [props.settings?.feedback],
    );

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Feedback saved!",
        onSave: (event, onDone) => {
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        feedback: {
                            type: properties.feedback.value,
                            customMessage: properties.customMessage.value,
                            shouldAllowComments: properties.shouldAllowComments.value,
                            responses: properties.responses.value?.map(({ value, message }) => ({ value, message })) ?? [],
                        },
                    },
                });
                onDone();
            })();
        },
    });

    const responseMap = properties.responses.value?.toIdMap("value") ?? Object.create(null);

    const setResponseType = (value: number, message: string) => {
        const current = responseMap[value];
        if (!current) {
            responseMap[value] = Object.create(null);
        }
        responseMap[value].value = value;
        responseMap[value].message = message;
        properties.responses.setValue(Object.values(responseMap));
    };

    const hasSelected = properties.feedback.value && properties.feedback.value !== FeedbackType.None;
    const feedbackOptions = FEEDBACK_TYPES[properties.feedback.value ?? FeedbackType.None];

    return (
        <>
            <SettingsContainer navigation="none">
                <SettingsContainer.Section
                    title="Feedback"
                    helpText="Feedback can be displayed to end users when tickets are closed."
                >
                    <SettingsContainer.Setting
                        title="Type"
                        description="Select a type of feedback, which will be displayed after
                            the ticket is completed and the agent clicks End Chat (the first time)."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Select
                            placeholder=""
                            onChange={value => properties.feedback.setValue(value.value as FeedbackType)}
                            options={OPTIONS}
                            size="medium"
                            value={OPTIONS.find(option => option.value === properties.feedback.value)}
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Custom Question"
                        description="When feedback is requested, instead of displaying the default question,
                            this custom message will be displayed."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isVisible={hasSelected}
                        isLoading={props.isLoading}
                    >
                        <Editor
                            value={properties.customMessage.value}
                            size="medium"
                            hasBorder
                            onChange={properties.customMessage.setValue}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Allow Comments"
                        description="If Enabled, the user also will be able to add a custom text message with the feedback."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isVisible={hasSelected}
                        isLoading={props.isLoading}
                    >
                        <Toggle
                            checked={properties.shouldAllowComments.value}
                            size="medium"
                            onChange={event => properties.shouldAllowComments.setValue(event.target.checked)}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Feedback Message"
                        description="Customize the message that Agent will display after
                        a user rates the feedback with the answer provided."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isVisible={hasSelected}
                        isLoading={props.isLoading}
                    >
                        {feedbackOptions?.valueLabels.map((label, index) => {
                            const response = responseMap[index];

                            return (
                                <div className={classNames(
                                    Styles["feedback-row"],
                                    Styles[`feedback-row-${properties.feedback.value?.toLowerCase()}`],
                                )}
                                >
                                    <div className={Styles["feedback-label"]}>
                                        {label}
                                    </div>
                                    <Editor
                                        rootClassName={Styles.editor}
                                        value={response?.message as string ?? "<p></p>"}
                                        onChange={data => setResponseType(index, data)}
                                        placeholder="Thanks for your feedback."
                                        size="small"
                                    />

                                </div>
                            );
                        })}

                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

            </SettingsContainer>
            {saveElement}
        </>

    );
}
